import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { activityById, defaultPlan, freshPlan } from '../data/activityCatalog';
import { activityLogSeed } from '../data/activityLogSeed';
import { autoPrompts } from '../data/autoPrompts';
import type { ActivityGoal, ActivityPlan, LogActivityInput, LoggedActivity } from '../types/activity';
import { useToast } from './ToastContext';
export type Persona = 'established' | 'fresh';
interface ActivityPlanContextValue {
  persona: Persona;
  setPersona: (persona: Persona) => void;
  plan: ActivityPlan;
  logs: LoggedActivity[];
  activeGoals: ActivityGoal[];
  savePlan: (next: {
    focus: string;
    workdays: number;
    goals: ActivityGoal[];
  }) => void;
  logActivity: (input: LogActivityInput) => void;
  undoLastLog: () => void;
  lastLog: LoggedActivity | null;
  openPromptIds: string[];
  completePrompt: (promptId: string) => void;
  snoozePrompt: (promptId: string) => void;
}
const ActivityPlanContext = createContext<ActivityPlanContextValue | null>(null);
function nowTimeLabel(): string {
  return new Date().toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit'
  });
}
export function ActivityPlanProvider({
  children,
  onboarded = true
}: {
  children: React.ReactNode;
  onboarded?: boolean;
}) {
  const {
    showToast
  } = useToast();
  const initialPersona: Persona = onboarded ? 'established' : 'fresh';
  const [persona, setPersonaState] = useState<Persona>(initialPersona);
  const [plan, setPlan] = useState<ActivityPlan>(initialPersona === 'fresh' ? freshPlan : defaultPlan);
  const [logs, setLogs] = useState<LoggedActivity[]>(initialPersona === 'fresh' ? [] : activityLogSeed);
  const [lastLog, setLastLog] = useState<LoggedActivity | null>(null);
  const [resolvedPromptIds, setResolvedPromptIds] = useState<string[]>([]);
  const setPersona = useCallback((next: Persona) => {
    setPersonaState(next);
    setPlan(next === 'fresh' ? freshPlan : defaultPlan);
    setLogs(next === 'fresh' ? [] : activityLogSeed);
    setLastLog(null);
    setResolvedPromptIds([]);
    showToast(next === 'fresh' ? 'Switched to a brand-new officer with no plan yet.' : 'Switched back to Jordan Reyes’ established plan.');
  }, [showToast]);
  const savePlan = useCallback((next: {
    focus: string;
    workdays: number;
    goals: ActivityGoal[];
  }) => {
    setPlan({
      focus: next.focus,
      workdays: next.workdays,
      goals: next.goals,
      onboarded: true,
      lastUpdatedLabel: 'Updated just now'
    });
    showToast('Perfect week saved. Today’s priorities are updated.');
  }, [showToast]);
  const logActivity = useCallback((input: LogActivityInput) => {
    const definition = activityById[input.category];
    const entry: LoggedActivity = {
      id: `log-${Date.now()}`,
      category: input.category,
      count: input.count,
      daysAgo: 0,
      timeLabel: nowTimeLabel(),
      contact: input.contact,
      channel: input.channel,
      note: input.note,
      source: input.source ?? 'manual'
    };
    setLogs(current => [entry, ...current]);
    setLastLog(entry);
    showToast(`Logged ${input.count} ${definition.unit} · ${definition.shortLabel}${input.contact ? ` · ${input.contact.name}` : ''}`);
  }, [showToast]);
  const undoLastLog = useCallback(() => {
    if (!lastLog) return;
    setLogs(existing => existing.filter(log => log.id !== lastLog.id));
    setLastLog(null);
    showToast('Last entry removed.');
  }, [lastLog, showToast]);
  const completePrompt = useCallback((promptId: string) => {
    const prompt = autoPrompts.find(item => item.id === promptId);
    if (!prompt) return;
    setResolvedPromptIds(current => [...current, promptId]);
    logActivity({
      category: prompt.category,
      count: 1,
      contact: prompt.contact,
      note: prompt.title,
      source: 'prompt'
    });
  }, [logActivity]);
  const snoozePrompt = useCallback((promptId: string) => {
    setResolvedPromptIds(current => [...current, promptId]);
    showToast('Prompt moved to tomorrow.');
  }, [showToast]);
  const value = useMemo<ActivityPlanContextValue>(() => ({
    persona,
    setPersona,
    plan,
    logs,
    activeGoals: plan.goals.filter(goal => goal.enabled),
    savePlan,
    logActivity,
    undoLastLog,
    lastLog,
    openPromptIds: persona === 'fresh' ? [] : autoPrompts.filter(prompt => !resolvedPromptIds.includes(prompt.id)).map(prompt => prompt.id),
    completePrompt,
    snoozePrompt
  }), [persona, setPersona, plan, logs, savePlan, logActivity, undoLastLog, lastLog, resolvedPromptIds, completePrompt, snoozePrompt]);
  return <ActivityPlanContext.Provider value={value} data-id="element-606" data-mp="3lucz46hmt6ih">
      {children}
    </ActivityPlanContext.Provider>;
}
export function useActivityPlan(): ActivityPlanContextValue {
  const context = useContext(ActivityPlanContext);
  if (!context) {
    throw new Error('useActivityPlan must be used within ActivityPlanProvider');
  }
  return context;
}